/**
 * BIM-style shape handles for a single selected wall in the 3D view.
 *
 * When exactly one wall is selected with the Select tool in a 3D view, three
 * drag handles appear on the wall body:
 *  - two endpoint grips (mid-height, at the location-line ends): drag to
 *    extend / shrink / re-angle the run. Endpoints resolve on the ground
 *    plane through the shared drag resolver (`resolveEndpointDragTarget`:
 *    drafting snap pipeline + junction OSNAP + own-axis lock).
 *    After commit, joint profiles with connected walls are re-solved
 *    (`processWallJoins` + `WallJoinSolver`).
 *  - one top shape handle (cone above the top face): drag vertically to
 *    raise/lower the wall. Dragging the top of a level-constrained wall
 *    detaches it, exactly like BIM (topConstraintId -> 'Unconnected').
 *
 * LIVE PREVIEW (performant by construction): the wall body follows the cursor
 * frame-by-frame. Drag frames write the in-progress geometryData to the tiny
 * `wallEditPreviewStore` (rAF-coalesced — at most one rebuild per animation
 * frame); WallRenderer prefers it for the dragged wall ONLY, so nothing else
 * (instance batching, opening indexes, other renderers) re-renders during the
 * drag and no kernel/undo traffic happens per frame. On release the final
 * target rebuilds through the SAME pure builder and commits ONCE through the
 * kernel (single undo step); endpoint drags then re-solve joins exactly.
 *
 * Handles are hidden for multi-selection, in plan views, and while any
 * tool other than SELECT is active. Handle meshes carry
 * userData.isWallHandle so the pick pipeline never swallows them.
 *
 * Mounted inside the BIM viewport Canvas; CAD entities keep GripEditLayer,
 * this layer serves BIM walls only.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useThree, ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { useShallow } from 'zustand/react/shallow';
import { useAppStore } from '../state/store';
import { useBIMStore } from '../state/useBIMStore';
import { useWallEditPreviewStore } from '../state/wallEditPreviewStore';
import { WallElement, WallGeometryData } from '../core/types';
import { applyJunctionStretch } from '../utils/JunctionMoveUtils';
import { GROUND_PLANE } from '../core/scene';
import {
    AxisGuideState,
    DragTarget,
    HANDLE_COLOR,
    HANDLE_SIZE,
    HeightGuideState,
    buildPreviewGeometry,
    dragSnapTolerance,
    heightGuideExtent,
    recomputeWallJoins,
    resolveEndpointDragTarget,
} from '../utils/wallHandlePreview';
import { WallAxisGuide, WallHeightGuide } from './WallHandleGuides';

type WallHandleKind = 'start' | 'end' | 'top';

interface WallHandle {
    kind: WallHandleKind;
    position: [number, number, number];
}

/** Module-level scratch for the drag ray (device-rate event path). */
const _ndc = new THREE.Vector2();
const _hit = new THREE.Vector3();

function HandleMarker({ handle, onDown }: { handle: WallHandle; onDown: (h: WallHandle, e: ThreeEvent<PointerEvent>) => void }) {
    return (
        <group position={handle.position}>
            {/* Invisible enlarged hit box keeps the small handle grabbable. */}
            <mesh
                onPointerDown={(e) => { e.stopPropagation(); onDown(handle, e); }}
                onPointerMove={(e) => e.stopPropagation()}
                userData={{ isWallHandle: true }}
            >
                <boxGeometry args={[HANDLE_SIZE * 3, HANDLE_SIZE * 3, HANDLE_SIZE * 3]} />
                <meshBasicMaterial transparent opacity={0} depthWrite={false} />
            </mesh>
            {handle.kind === 'top' ? (
                // Cone geometry grows along +Y — rotate onto world +Z (up).
                <mesh rotation={[Math.PI / 2, 0, 0]} userData={{ isWallHandle: true }}>
                    <coneGeometry args={[HANDLE_SIZE, HANDLE_SIZE * 2.2, 12]} />
                    <meshBasicMaterial color={HANDLE_COLOR} />
                </mesh>
            ) : (
                <mesh userData={{ isWallHandle: true }}>
                    <sphereGeometry args={[HANDLE_SIZE, 12, 12]} />
                    <meshBasicMaterial color={HANDLE_COLOR} />
                </mesh>
            )}
        </group>
    );
}

export function WallShapeHandleLayer() {
    const tool = useAppStore((s) => s.activeTool);
    const viewType = useAppStore((s) => s.view.viewType);
    const appSelection = useAppStore((s) => s.selectedIds);
    const bimSelection = useBIMStore((s) => s.selection);
    const { camera, gl, raycaster, invalidate } = useThree();

    // Exactly one selected element, and it must be a wall; otherwise no
    // handles (multi-select keeps BIM's plain highlight behavior).
    const wall = useBIMStore(useShallow((s) => {
        const ids = new Set<string>([...appSelection, ...bimSelection]);
        if (ids.size !== 1) return null;
        const el = s.project.elements.get([...ids][0]);
        return el && el.category === 'Walls' ? (el as WallElement) : null;
    }));

    const enabled = tool === 'select' && viewType === '3d' && !!wall;

    // The dragged wall's own in-progress geometry (null unless a drag runs on
    // it): handles track the live preview instead of the committed data.
    const previewGeometry = useWallEditPreviewStore((s) => (wall && s.wallId === wall.id ? s.geometryData : null));
    const g = previewGeometry ?? wall?.geometryData;
    const handles = useMemo<WallHandle[]>(() => {
        if (!g) return [];
        const height = g.height ?? 3;
        const midZ = (g.baseOffset || 0) + height / 2;
        const topZ = (g.baseOffset || 0) + height + 0.18;
        const midX = (g.start[0] + g.end[0]) / 2;
        const midY = (g.start[1] + g.end[1]) / 2;
        return [
            { kind: 'start', position: [g.start[0], g.start[1], midZ] },
            { kind: 'end', position: [g.end[0], g.end[1], midZ] },
            { kind: 'top', position: [midX, midY, topZ] },
        ];
    }, [g]);

    // BIM-style snap guides (render state): the own-axis guide is visible
    // for the whole endpoint drag, the height guide only while an elevation
    // alignment candidate is within tolerance. Hooks stay above the early
    // return — the layer mounts/unmounts as handles appear and disappear.
    const [axisGuide, setAxisGuide] = useState<AxisGuideState | null>(null);
    const [heightGuide, setHeightGuide] = useState<HeightGuideState | null>(null);

    // Live drag: rAF-coalesced preview writes to the tiny preview store, ONE
    // kernel commit on release. The original geometry is snapshotted at
    // pointer-down so every frame (and the commit) rebuilds from the same
    // pristine source — no accumulation drift, no undo entries per frame.
    const dragRef = useRef<{
        kind: WallHandleKind;
        wallId: string;
        origGeom: WallGeometryData;
        originHeight: number;
        originEndpoint: [number, number];
        raf: number | null;
        lastEvent: PointerEvent | null;
    } | null>(null);

    useEffect(() => () => {
        // Unmount mid-drag (tool switch, selection change): drop the preview.
        const d = dragRef.current;
        if (d?.raf != null) cancelAnimationFrame(d.raf);
        dragRef.current = null;
        useWallEditPreviewStore.getState().clear();
    }, []);

    if (!enabled || handles.length === 0) return null;

    const handleDown = (handle: WallHandle, _e: ThreeEvent<PointerEvent>) => {
        if (!wall || dragRef.current) return;
        const origGeom: WallGeometryData = structuredClone(wall.geometryData);
        const kind = handle.kind;
        dragRef.current = {
            kind,
            wallId: wall.id,
            origGeom,
            originHeight: origGeom.height ?? 3,
            originEndpoint: kind === 'start'
                ? [origGeom.start[0], origGeom.start[1]]
                : [origGeom.end[0], origGeom.end[1]],
            raf: null,
            lastEvent: null,
        };

        // --- Alignment context (BIM-style), snapshotted at drag start ---
        // Snap tolerance scales with camera distance (1.2% of it) so the
        // snap feel stays constant across zoom levels.
        const wallMid = new THREE.Vector3(
            (origGeom.start[0] + origGeom.end[0]) / 2,
            (origGeom.start[1] + origGeom.end[1]) / 2,
            origGeom.baseOffset || 0
        );
        const snapTol = dragSnapTolerance(camera, wallMid);

        // Own-axis guide (endpoint drags): the infinite run line drawn
        // extended past both ends. Snapping onto it keeps the direction and
        // changes pure length — "extend the wall along its own axis".
        if (kind !== 'top') {
            const isStart = kind === 'start';
            const fx = isStart ? origGeom.end[0] : origGeom.start[0];
            const fy = isStart ? origGeom.end[1] : origGeom.start[1];
            const mx = isStart ? origGeom.start[0] : origGeom.end[0];
            const my = isStart ? origGeom.start[1] : origGeom.end[1];
            const runLen = Math.hypot(mx - fx, my - fy);
            if (runLen > 1e-6) {
                const dirX = (mx - fx) / runLen;
                const dirY = (my - fy) / runLen;
                const ext = Math.max(1.0, runLen * 0.6);
                const gz = (origGeom.baseOffset || 0) + 0.01;
                setAxisGuide({
                    points: [
                        [fx - dirX * ext, fy - dirY * ext, gz],
                        [mx + dirX * ext, my + dirY * ext, gz],
                    ],
                    snapped: false,
                });
            }
        } else {
            setAxisGuide(null);
        }
        setHeightGuide(null);

        // Height candidates (top drags): every other wall's top elevation
        // plus the project levels — the BIM alignment set for shape handles.
        const heightCandidates: number[] = [];
        if (kind === 'top') {
            const bim = useBIMStore.getState();
            bim.project.elements.forEach((el) => {
                if (el.category !== 'Walls' || el.id === wall.id) return;
                const og = (el as any).geometryData;
                if (!og) return;
                heightCandidates.push((og.baseOffset || 0) + (og.height ?? 3));
            });
            bim.project.levels.forEach((lvl: any) => {
                const elev = typeof lvl?.elevation === 'number'
                    ? lvl.elevation
                    : lvl?.geometryData?.elevation;
                if (typeof elev === 'number') heightCandidates.push(elev / 1000);
            });
        }

        /** Pointer -> rounded drag target (ground plane or wall mid-plane). */
        const computeTarget = (ev: PointerEvent): DragTarget | null => {
            const rect = gl.domElement.getBoundingClientRect();
            if (rect.width === 0 || rect.height === 0) return null;
            _ndc.set(
                ((ev.clientX - rect.left) / rect.width) * 2 - 1,
                -((ev.clientY - rect.top) / rect.height) * 2 + 1
            );
            camera.updateMatrixWorld();
            raycaster.setFromCamera(_ndc, camera);
            if (kind === 'top') {
                // Vertical plane through the wall mid-line: take the world Z.
                const dx = origGeom.end[0] - origGeom.start[0];
                const dy = origGeom.end[1] - origGeom.start[1];
                const len = Math.hypot(dx, dy) || 1;
                const normal = new THREE.Vector3(dy / len, -dx / len, 0);
                const mid = new THREE.Vector3(
                    (origGeom.start[0] + origGeom.end[0]) / 2,
                    (origGeom.start[1] + origGeom.end[1]) / 2, 0
                );
                const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(normal, mid);
                if (Math.abs(raycaster.ray.direction.dot(normal)) < 0.02) return null;
                const topHit = raycaster.ray.intersectPlane(plane, _hit);
                if (!topHit) return null;
                // Alignment first (BIM): lock onto the nearest elevation
                // candidate within tolerance — another wall's top or a level.
                // Otherwise free with 1 cm rounding; both paths shared by
                // preview and commit (zero pop).
                let best: number | null = null;
                let bestDist = snapTol;
                for (const cz of heightCandidates) {
                    const d = Math.abs(cz - topHit.z);
                    if (d <= bestDist) {
                        bestDist = d;
                        best = cz;
                    }
                }
                if (best != null) {
                    return {
                        height: Math.max(0.1, Math.round((best - (origGeom.baseOffset || 0)) * 100) / 100),
                        snappedZ: Math.round(best * 100) / 100,
                    };
                }
                return { height: Math.max(0.1, Math.round((topHit.z - (origGeom.baseOffset || 0)) * 100) / 100) };
            }
            const groundHit = raycaster.ray.intersectPlane(GROUND_PLANE, _hit);
            if (!groundHit) return null;
            // Shared endpoint resolution: drafting snap pipeline + junction
            // OSNAP (other walls' endpoints) + own-axis lock; mm rounding,
            // shared by preview and commit (zero pop). Off both locks the
            // drag stays free 2D (the wall may re-angle).
            const res = resolveEndpointDragTarget(
                _hit,
                origGeom,
                kind === 'start' ? 'start' : 'end',
                camera,
                gl,
                snapTol,
                wall.id
            );
            return { endpoint: res.endpoint, axisSnapped: res.axisSnapped || res.endpointSnapped };
        };

        const applyFrame = (ev: PointerEvent) => {
            const drag = dragRef.current;
            if (!drag) return;
            const target = computeTarget(ev);
            if (!target) return;
            useWallEditPreviewStore.getState().set(drag.wallId, buildPreviewGeometry(drag.origGeom, drag.kind, target));
            // Guide feedback follows the same target the preview used.
            if (drag.kind === 'top') {
                setHeightGuide(target.snappedZ != null ? heightGuideExtent(drag.origGeom, target.snappedZ) : null);
            } else {
                const snapped = target.axisSnapped === true;
                setAxisGuide((prev) => (prev && prev.snapped !== snapped ? { ...prev, snapped } : prev));
            }
        };

        const onMove = (ev: PointerEvent) => {
            const drag = dragRef.current;
            if (!drag) return;
            drag.lastEvent = ev;
            if (drag.raf == null) {
                // Coalesce the pointer-move storm: at most one geometry
                // rebuild + preview write per animation frame.
                drag.raf = requestAnimationFrame(() => {
                    const d = dragRef.current;
                    if (d) d.raf = null;
                    if (d?.lastEvent) applyFrame(d.lastEvent);
                });
            }
            invalidate();
        };

        const removeListeners = () => {
            window.removeEventListener('pointermove', onMove, true);
            window.removeEventListener('pointerup', onUp, true);
            window.removeEventListener('pointercancel', onCancel, true);
        };

        const teardown = () => {
            const drag = dragRef.current;
            dragRef.current = null;
            if (drag?.raf != null) cancelAnimationFrame(drag.raf);
            useWallEditPreviewStore.getState().clear();
            setHeightGuide(null);
            setAxisGuide(null);
            removeListeners();
            return drag;
        };

        const onCancel = () => { teardown(); };

        const onUp = (ev: PointerEvent) => {
            const drag = teardown();
            if (!drag) return;
            const target = computeTarget(ev);
            if (!target) return;
            const moved = drag.kind === 'top'
                ? Math.abs((target.height ?? drag.originHeight) - drag.originHeight) > 1e-9
                : Math.hypot(
                    target.endpoint![0] - drag.originEndpoint[0],
                    target.endpoint![1] - drag.originEndpoint[1]
                ) > 1e-9;
            if (!moved) return;
            const bim = useBIMStore.getState();
            if (!bim.project.elements.has(drag.wallId)) return;
            // Commit the SAME pure build the preview frames used — the
            // committed geometry equals the last previewed frame.
            const finalGeometry = buildPreviewGeometry(drag.origGeom, drag.kind, target);
            bim.updateElement(drag.wallId, {
                geometryData: finalGeometry,
            } as any);
            // Endpoints drive the join geometry — re-solve with neighbors.
            if (drag.kind !== 'top') {
                // Joint carry (SketchUp shared-vertex edit): objects welded
                // to the dragged endpoint follow the same delta — the wall
                // chain stays connected instead of tearing apart. The dragged
                // wall is excluded (it just committed its own geometry).
                const oldEnd = drag.kind === 'start' ? drag.origGeom.start : drag.origGeom.end;
                const newEnd = drag.kind === 'start' ? finalGeometry.start : finalGeometry.end;
                const delta = new THREE.Vector3(
                    newEnd[0] - oldEnd[0],
                    newEnd[1] - oldEnd[1],
                    (newEnd[2] ?? 0) - (oldEnd[2] ?? 0)
                );
                if (delta.lengthSq() > 1e-12) {
                    const carried = applyJunctionStretch(
                        new THREE.Vector3(oldEnd[0], oldEnd[1], oldEnd[2] ?? 0),
                        delta,
                        'Drag Wall Endpoint',
                        { excludeId: drag.wallId }
                    );
                    if (carried > 0) {
                        console.log('Wall handle:', carried, 'joined object(s) carried with the endpoint');
                    }
                }
                recomputeWallJoins(drag.wallId);
            }
            invalidate();
        };

        window.addEventListener('pointermove', onMove, true);
        window.addEventListener('pointerup', onUp, true);
        window.addEventListener('pointercancel', onCancel, true);
    };

    return (
        <group>
            {handles.map((h) => (
                <HandleMarker key={h.kind} handle={h} onDown={handleDown} />
            ))}
            {/* Own-axis extension guide (endpoint drags) and height
                alignment guide (top drags) — shared overlays. */}
            {axisGuide && <WallAxisGuide guide={axisGuide} />}
            {heightGuide && <WallHeightGuide guide={heightGuide} />}
        </group>
    );
}
